import axios from 'axios';
// @ts-ignore
import { ParseCoordinates } from '../Utils/CountryUtility';
import React from 'react';
const backendUrl = process.env.REACT_APP_BACKEND_URL;

export interface SightseeingPost {
  id: number;
  iso: string;
  region: string;
  place: string;
  activity: string;
  travel_category: string;
  description: string;
  images: string[]; // URLs from img.get_image_url()
  attributions: string[]; // URLs from img.get_attribution()
  coordinates: string;
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
}

export interface SightseeingApiResponse {
  results: SightseeingPost[];
  total_pages: number;
  current_page: number;
}

export type TFetchSightseeing = (
  iso: string,
  options: {
    size?: number; // page size
    cursor?: string; // cursor for pagination
    cursorToken?: string; // server-issued token bound to cursor context
    category?: string; // filter
    region?: string; // filter
    selectedItemId?: number; // item to scroll to (only used on initial load)
  },
  setOldCursor: (nextCursor: string) => void,
  setNewCursor: (previousCursor: string) => void,
  setCurrentCursor: (cursor: string) => void,
  setCursorToken: (cursorToken: string) => void,
  setCategory: (category: string) => void,
  setRegion: (region: string) => void,
  setCurrentPage: (currentPage: number) => void,
  setSightseeingData: (data: SightseeingPost[]) => void,
  setMapCenter: (coords: [number, number]) => void,
  setActivePost: (index: number) => void,
  setActiveMarker: (index: number) => void,
  itemRefs: React.RefObject<(HTMLDivElement | null)[]>,
) => void;

export function FetchNumberOfSightseeingPosts(iso?: string): Promise<number> {
  // Determine the URL based on whether 'iso' is provided
  const url = iso ? `${backendUrl}/worlddata/sightseeing/get-sightseeing-counter/${iso}` : `${backendUrl}/worlddata/sightseeing/get-sightseeing-total-counter/`;

  // Make the API request and return the number of posts
  return axios
    .get(url)
    .then((response) => {
      return response.data.sightseeing_counter || response.data.sightseeing_counter_total; // Return the number of posts
    })
    .catch((error) => {
      console.error('Error fetching sightseeing posts:', error);
      return 0; // Return 0 if there's an error
    });
}

export async function FetchRandomSightseeing(size: number, cache_key: string, iso?: string, travel_category?: string): Promise<any[]> {
  try {
    let url = `${backendUrl}/worlddata/sightseeing/get-random-sightseeing-data/${size}/?cache_key=${cache_key}`;
    if (iso) url += `&iso=${iso}`;
    if (travel_category) url += `&travel_category=${travel_category}`;

    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching sightseeing data:', error);
    throw error;
  }
}

export async function FetchRandomSightseeingBulkCategory(size: number, cache_key: string, iso?: string): Promise<Record<string, any[]>> {
  try {
    let url = `${backendUrl}/worlddata/sightseeing/get-random-sightseeing-data-bulk-category/${size}/?cache_key=${cache_key}`;
    if (iso) url += `&iso=${iso}`;

    const response = await axios.get(url);
    return response.data; // shape: { "Nature": [...], "Art": [...], "Architecture": [...], ... }
  } catch (error) {
    console.error('Error fetching bulk sightseeing data:', error);
    throw error;
  }
}

export function FetchNumberOfSightseeingPostsPerContinent() {
  const url = `${backendUrl}/worlddata/sightseeing/get-sightseeing-continent-counter/`;

  return axios.get(url)
    .then((response) => response.data.sightseeing_counter_continent || {})
    .catch((error) => {
      console.error('Error fetching sightseeing posts:', error);
      return {};
    });
}

export const FetchSightseeing: TFetchSightseeing = async (
  iso,
  { size = 9, cursor = '', cursorToken = '', category = '', region = '', selectedItemId = null },
  setOldCursor,
  setNewCursor,
  setCurrentCursor,
  setCursorToken,
  setCategory,
  setRegion,
  setCurrentPage,
  setSightseeingData,
  setMapCenter,
  setActivePost,
  setActiveMarker,
  itemRefs,
) => {
  const effectiveSize = Number.isFinite(size) && size > 0 ? size : 9;
  const cursorParam = cursor ? `&cursor=${cursor}` : '';
  const cursorTokenParam = cursor && cursorToken ? `&cursor_token=${encodeURIComponent(cursorToken)}` : '';
  const categoryParam = category ? `&travel_category=${category}` : '';
  const regionParam = region ? `&region=${encodeURIComponent(region)}` : '';
  const isoParam = iso ? `iso=${encodeURIComponent(iso)}` : '';
  const extraParams = `${cursorParam}${cursorTokenParam}${categoryParam}${regionParam}`;
  const queryTail = `${isoParam}${extraParams}`;
  const url = `${backendUrl}/worlddata/sightseeing/get-sightseeing-data/${effectiveSize}/?${queryTail}`;

  try {
    const response = await axios.get(url);

    const items = response.data.items;

    setOldCursor(response.data.old_cursor);
    setNewCursor(response.data.new_cursor);
    setCurrentCursor(cursor);
    setCursorToken(response.data.cursor_token || '');
    setCategory(category);
    setRegion(region);
    setCurrentPage(response.data.page);
    setSightseeingData(items);

    if (items.length > 0) {
      const [firstPost] = items;
      const [lat, lon] = ParseCoordinates(firstPost.coordinates);
      setMapCenter([lat, lon]);
    }

    if (selectedItemId) {
      const index = items.findIndex((item: SightseeingPost) => item.id === Number(selectedItemId));

      if (index !== -1) {
        setActivePost(index);
        setActiveMarker(index);

        setTimeout(() => {
          itemRefs.current?.[index]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }
  } catch (error) {
    console.error('Error fetching sightseeing data:', error);
  }
};
